import React, { useState, useEffect } from "react";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import {
    Search,
    Plus,
    Package,
    Trash2,
    Edit2,
    Layers,
    Wrench,
    FileSpreadsheet,
    ChevronLeft,
    ChevronRight,
    CheckSquare,
    Square,
    AlertCircle,
    Filter,
    ClipboardList,
    RefreshCcw
} from "lucide-react";
import { Button } from "../ui/button";
import toast from "react-hot-toast";
import ItemCatalogoModal from "./ItemCatalogoModal";
import ItemCatalogoImportModal from "./ItemCatalogoImportModal";

interface ItemCatalogo {
    id: any; // ID para React (puede ser numérico o 'pi-X')
    id_catalog?: number; // El ID real del catálogo si es un item de plan
    codigo_interno: string;
    nombre: string;
    descripcion: string;
    categoria: string;
    tipo: "CONSUMIBLE" | "SERVICIO";
    unidad_medida: string;
    precio_unitario: number;
    stock_minimo: number;
    especificaciones: string;
    activo: boolean;
    empresa_id: number | null;
    en_uso_plan?: boolean;
    en_uso_ot?: boolean;
    planes_asociados?: string[];
}

const CATEGORIAS_VALIDAS = [
    "FILTROS",
    "FLUIDOS",
    "LUBRICANTES",
    "FRENOS",
    "SUSPENSION",
    "MOTOR",
    "ELECTRICO",
    "NEUMATICOS",
    "CARROCERIA",
    "PARTES",
    "INSUMOS",
    "MANO_OBRA",
    "TRANSMISION",
    "OTROS",
];

const CatalogoItems: React.FC = () => {
    const [items, setItems] = useState<ItemCatalogo[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterTipo, setFilterTipo] = useState<string>("ALL");
    const [filterCategoria, setFilterCategoria] = useState<string>("ALL");
    const [filterPlan, setFilterPlan] = useState<string>("ALL");
    const [availablePlans, setAvailablePlans] = useState<string[]>([]);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<ItemCatalogo | null>(null);
    const [itemToDelete, setItemToDelete] = useState<ItemCatalogo | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    const [selectedIds, setSelectedIds] = useState<any[]>([]);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [usageFilter, setUsageFilter] = useState<"ALL" | "IN_PLAN" | "IN_OT" | "NO_PLAN">("IN_PLAN");
    const [deleteFeedback, setDeleteFeedback] = useState<{
        message: string,
        planes?: string[],
        detalles?: Record<string, string[]>
    } | null>(null);

    // Estados de paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        loadItems();
    }, [usageFilter]); // Recargar al cambiar de vista para obtener datos explotados si es necesario

    const loadItems = async (force: boolean = false) => {
        try {
            if (items.length === 0 || force) {
                setLoading(true);
            }
            const params: any = {};
            if (usageFilter === 'IN_PLAN') {
                params.view = 'by_plan'; // Activar modo explotado
            }

            const response = await maintenanceService.getItemsCatalogo(params);
            if (response.success) {
                setItems(response.data);

                // Extraer planes únicos disponibles para el filtro
                const plans = new Set<string>();
                response.data.forEach((item: any) => {
                    if (item.planes_asociados) {
                        item.planes_asociados.forEach((p: string) => plans.add(p));
                    }
                });
                setAvailablePlans(Array.from(plans).sort());
            }
        } catch (error) {
            console.error("Error loading items:", error);
            toast.error("Error al cargar catálogo de ítems");
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        await loadItems(true);
        toast.success("Catálogo actualizado");
    };

    const filteredItems = items.filter((item) => {
        const matchesSearch =
            item.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.codigo_interno.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.categoria.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesTipo = filterTipo === "ALL" || item.tipo === filterTipo;
        const matchesCategoria = filterCategoria === "ALL" || item.categoria === filterCategoria;
        const matchesPlan = filterPlan === "ALL" || (item.planes_asociados && item.planes_asociados.includes(filterPlan));

        const matchesUsage =
            usageFilter === "ALL" ? true :
                usageFilter === "IN_PLAN" ? item.en_uso_plan :
                    usageFilter === "IN_OT" ? item.en_uso_ot :
                        (!item.en_uso_plan && !item.en_uso_ot);

        const matchesCompany = item.empresa_id !== null || item.en_uso_plan || item.en_uso_ot;

        return matchesSearch && matchesTipo && matchesCategoria && matchesPlan && matchesUsage && matchesCompany;
    });

    // Paginación
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = filteredItems.slice(startIndex, endIndex);

    // Resetear a página 1 cuando cambian los filtros
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterTipo, filterCategoria, filterPlan]);

    const handleAdd = () => {
        setSelectedItem(null);
        setIsModalOpen(true);
    };

    const handleEdit = (item: ItemCatalogo) => {
        // Si es un item de plan, editar el item base del catálogo
        const actualItem = {
            ...item,
            id: item.id_catalog || item.id
        };
        setSelectedItem(actualItem);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (item: ItemCatalogo) => {
        setItemToDelete(item);
    };

    const handleImport = () => {
        setIsImportModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        try {
            setIsDeleting(true);
            setDeleteFeedback(null);

            // Siempre borrar el item base del catálogo
            const targetId = itemToDelete.id_catalog || itemToDelete.id;

            const response = await maintenanceService.deleteItemCatalogo(
                targetId,
            );
            if (response.success) {
                toast.success("Ítem eliminado correctamente");
                setItemToDelete(null);
                loadItems();
            }
        } catch (error: any) {
            console.error("Error deleting item:", error);
            if (error.response?.status === 422 && error.response.data?.error_code === 'ITEM_IN_USE') {
                setDeleteFeedback({
                    message: error.response.data.message,
                    planes: error.response.data.detalles // Mapear detalles del backend a la lista para mostrar
                });
            } else {
                toast.error("Error al eliminar el ítem");
            }
        } finally {
            setIsDeleting(false);
        }
    };

    const goToPage = (page: number) => {
        setCurrentPage(page);
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Funciones de selección
    const toggleSelect = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
        );
    };

    const toggleSelectAllPage = () => {
        const pageIds = currentItems.map((item) => item.id);
        const allSelected = pageIds.every((id) => selectedIds.includes(id));

        if (allSelected) {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        } else {
            setSelectedIds((prev) => {
                const combined = [...prev, ...pageIds];
                return combined.filter((id, idx) => combined.indexOf(id) === idx);
            });
        }
    };

    const handleBulkDelete = async () => {
        try {
            setIsBulkDeleting(true);
            setDeleteFeedback(null);

            // Extraer IDs de catálogo reales para borrado masivo
            const mappedIds = selectedIds.map(id => {
                if (typeof id === 'string' && id.startsWith('pi-')) {
                    // Encontrar el item en el array para obtener su id_catalog
                    const found = items.find(it => it.id === id);
                    return found ? found.id_catalog : null;
                }
                return id;
            }).filter(id => id !== null);
            const realIds = mappedIds.filter((id, idx) => mappedIds.indexOf(id) === idx);

            const response = await maintenanceService.deleteMultipleItemsCatalogo(realIds);
            if (response.success) {
                toast.success(`${realIds.length} ítems del catálogo eliminados correctamente`);
                setSelectedIds([]);
                setShowBulkDeleteConfirm(false);
                loadItems();
            }
        } catch (error: any) {
            console.error("Error deleting multiple items:", error);
            if (error.response?.status === 422 && error.response.data?.error_code === 'ITEMS_IN_USE') {
                setDeleteFeedback({
                    message: error.response.data.message,
                    planes: error.response.data.items // Usamos planes para listar los nombres de los items bloqueados
                });
            } else {
                toast.error("Error al eliminar los ítems seleccionados");
            }
        } finally {
            setIsBulkDeleting(false);
        }
    };



    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">
            {/* Toolbar */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 transition-all hover:shadow-md space-y-5">
                {/* Search and Main Actions */}
                <div className="flex flex-col lg:flex-row items-center gap-4">
                    <div className="relative flex-1 group w-full">
                        <Search
                            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors"
                            size={18}
                        />
                        <input
                            placeholder="Buscar por código, nombre o categoría..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 h-11 bg-slate-50 border border-slate-200/50 hover:bg-slate-100/50 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all rounded-xl text-[13px] font-bold outline-none placeholder:text-slate-400"
                        />
                    </div>

                        <div className="flex items-center gap-2 flex-1 lg:flex-none">
                            <button
                                onClick={handleRefresh}
                                disabled={loading}
                                title="Recargar catálogo"
                                className="h-11 w-11 flex items-center justify-center bg-white border border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all rounded-xl shadow-sm active:scale-95"
                            >
                                <RefreshCcw size={18} className={loading ? "animate-spin" : ""} />
                            </button>

                            <Button
                                onClick={handleImport}
                                className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-lg shadow-emerald-200/40 border-none flex items-center gap-2 font-black text-[10px] uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-[0.98] flex-1 justify-center"
                            >
                                <FileSpreadsheet size={16} />
                                <span>Importar</span>
                            </Button>

                            <Button
                                onClick={handleAdd}
                                className="h-11 px-5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-200/40 border-none flex items-center gap-2 font-black text-[10px] uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-[0.98] flex-1 justify-center"
                            >
                                <Plus size={16} />
                                <span>Registrar</span>
                            </Button>
                        </div>
                </div>

                {/* Filters and Stats Row */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-slate-100">
                    <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                        {/* Filter Type Tabs */}
                        <div className="flex items-center gap-1 bg-slate-100/60 p-1 rounded-xl border border-slate-200/50 overflow-x-auto no-scrollbar max-w-full">
                            {[
                                { id: "ALL", label: "Todos", icon: Layers },
                                { id: "CONSUMIBLE", label: "Repuestos", icon: Package },
                                { id: "SERVICIO", label: "Servicios", icon: Wrench },
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setFilterTipo(tab.id as any)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap flex-1 sm:flex-none justify-center ${filterTipo === tab.id
                                        ? "bg-white text-indigo-600 shadow-sm border border-indigo-100"
                                        : "text-slate-500 hover:text-slate-900 hover:bg-white/40"
                                        }`}
                                >
                                    <tab.icon size={12} />
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Dropdown Filters Group */}
                        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
                            {/* Category Select */}
                            <div className="relative group w-full md:w-auto">
                                <Filter size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-indigo-500 transition-colors pointer-events-none" />
                                <select
                                    value={filterCategoria}
                                    onChange={(e) => setFilterCategoria(e.target.value)}
                                    className="w-full h-10 pl-9 pr-8 bg-slate-50 border border-slate-200/50 rounded-xl text-[9px] font-black text-slate-700 hover:bg-white hover:shadow-sm transition-all focus:ring-4 focus:ring-indigo-500/5 outline-none uppercase tracking-widest appearance-none cursor-pointer min-w-[170px]"
                                >
                                    <option value="ALL">TODAS LAS CATEGORÍAS</option>
                                    {CATEGORIAS_VALIDAS.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Plan Select */}
                            <div className="relative group w-full md:w-auto">
                                <ClipboardList size={12} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-indigo-500 transition-colors pointer-events-none" />
                                <select
                                    value={filterPlan}
                                    onChange={(e) => setFilterPlan(e.target.value)}
                                    className="w-full h-10 pl-9 pr-8 bg-slate-50 border border-slate-200/50 rounded-xl text-[9px] font-black text-slate-700 hover:bg-white hover:shadow-sm transition-all focus:ring-4 focus:ring-indigo-500/5 outline-none uppercase tracking-widest appearance-none cursor-pointer max-w-full md:max-w-[220px]"
                                >
                                    <option value="ALL">TODOS LOS PLANES</option>
                                    {availablePlans.map(plan => (
                                        <option key={plan} value={plan}>{plan}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Selector de Uso en Planes (3 estados) */}
                            <div className="flex items-center gap-1 bg-slate-100/60 p-1 rounded-xl border border-slate-200/50">
                                {[
                                    { id: "ALL", label: "Catálogo" },
                                    { id: "IN_PLAN", label: "En Planes" },
                                    { id: "IN_OT", label: "Histórico (OT)" },
                                    { id: "NO_PLAN", label: "Sin Uso" },
                                ].map((opt) => (
                                    <button
                                        key={opt.id}
                                        onClick={() => setUsageFilter(opt.id as any)}
                                        className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${usageFilter === opt.id
                                            ? "bg-white text-indigo-600 shadow-sm border border-indigo-100"
                                            : "text-slate-500 hover:text-slate-900 hover:bg-white/40"
                                            }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Stats Badge */}
                    <div className="flex items-center gap-1.5 px-4 py-2 bg-slate-50 border border-slate-200/50 rounded-xl w-full md:w-auto justify-center shadow-inner-sm shrink-0">
                        <Package size={14} className="text-slate-400" />
                        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400">Total:</span>
                        <span className="text-xs font-black text-slate-900">{filteredItems.length}</span>
                        <span className="text-[9px] font-black uppercase tracking-[0.2em] text-slate-400 ml-1">Items</span>
                    </div>
                </div>
            </div>

            {/* Content Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden relative">
                {/* Floating Action Bar for Selected Items */}
                {selectedIds.length > 0 && (
                    <div className="absolute top-0 left-0 right-0 z-20 bg-emerald-600 px-6 py-3 flex items-center justify-between animate-in slide-in-from-top duration-300">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-lg text-white">
                                <CheckSquare size={18} />
                            </div>
                            <span className="text-sm font-black text-white uppercase tracking-widest text-emerald-50">
                                {selectedIds.length} ÍTEMS SELECCIONADOS
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                onClick={() => setSelectedIds([])}
                                variant="ghost"
                                className="h-9 px-4 text-white hover:bg-white/10 font-bold text-[10px] uppercase tracking-widest"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={() => setShowBulkDeleteConfirm(true)}
                                className="h-9 px-6 bg-white text-emerald-600 hover:bg-emerald-50 rounded-lg shadow-xl font-black text-[10px] uppercase tracking-widest border-none transition-all active:scale-95"
                            >
                                <Trash2 size={14} className="mr-2" />
                                Eliminar Selección
                            </Button>
                        </div>
                    </div>
                )}

                <div className="overflow-x-auto no-scrollbar">
                    <table className="w-full text-left border-collapse min-w-[1000px]">
                        <thead>
                            <tr className="bg-slate-900">
                                <th className="px-6 py-4 w-12 border-b border-white/5">
                                    <div
                                        className="flex items-center justify-center cursor-pointer"
                                        onClick={toggleSelectAllPage}
                                    >
                                        {currentItems.length > 0 && currentItems.every(i => selectedIds.includes(i.id)) ? (
                                            <CheckSquare size={20} className="text-emerald-400" />
                                        ) : (
                                            <Square size={20} className="text-slate-500" />
                                        )}
                                    </div>
                                </th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-white/5">
                                    Código y Categoría
                                </th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-white/5">
                                    Nombre y Descripción
                                </th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-white/5 hidden sm:table-cell">
                                    Tipo
                                </th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-white/5 hidden lg:table-cell">
                                    U.M.
                                </th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-white/5 text-right hidden lg:table-cell">
                                    Precio Ref.
                                </th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-white/5 text-center">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {currentItems.map((item) => (
                                <tr
                                    key={item.id}
                                    className="group hover:bg-slate-50/50 transition-colors"
                                >
                                    <td className="px-6 py-4">
                                        <div
                                            className="flex items-center justify-center cursor-pointer"
                                            onClick={() => toggleSelect(item.id)}
                                        >
                                            {selectedIds.includes(item.id) ? (
                                                <CheckSquare size={20} className="text-emerald-600" />
                                            ) : (
                                                <Square size={20} className="text-slate-200 group-hover:text-emerald-300 transition-colors" />
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-black text-slate-900 text-sm group-hover:text-indigo-600 transition-colors">
                                                    {item.codigo_interno}
                                                </span>
                                                {item.en_uso_plan && (
                                                    <div className="flex flex-wrap gap-1">
                                                        <span
                                                            className="text-[9px] font-black bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded border border-emerald-100 uppercase tracking-tighter"
                                                            title="Este ítem es parte de un plan de mantenimiento"
                                                        >
                                                            Plan
                                                        </span>
                                                        {item.en_uso_ot && (
                                                            <span
                                                                className="text-[9px] font-black bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded border border-blue-100 uppercase tracking-tighter"
                                                                title="Este ítem ha sido usado en órdenes de trabajo"
                                                            >
                                                                OT
                                                            </span>
                                                        )}
                                                        {item.planes_asociados && item.planes_asociados.map((p, i) => (
                                                            <span key={i} className="text-[8px] font-bold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200 uppercase">
                                                                {p}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                {item.categoria}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="font-bold text-slate-700 text-sm">
                                                {item.nombre}
                                            </span>
                                            <span className="text-xs text-slate-400 line-clamp-1">
                                                {item.descripcion || "Sin descripción"}
                                            </span>
                                            {/* Extra info for mobile */}
                                            <div className="flex lg:hidden gap-3 mt-1.5">
                                                <span className="text-[9px] font-black text-slate-400 uppercase bg-slate-100 px-1.5 py-0.5 rounded tracking-widest">{item.unidad_medida}</span>
                                                <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded tracking-widest border border-emerald-100">S/ {item.precio_unitario?.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 hidden sm:table-cell">
                                        <span
                                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${item.tipo === "SERVICIO"
                                                ? "bg-amber-100 text-amber-700 border border-amber-200"
                                                : "bg-indigo-100 text-indigo-700 border border-indigo-200"
                                                }`}
                                        >
                                            <div
                                                className={`h-1.5 w-1.5 rounded-full ${item.tipo === "SERVICIO" ? "bg-amber-500" : "bg-indigo-500"}`}
                                            />
                                            {item.tipo}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 hidden lg:table-cell">
                                        <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded text-[10px] font-black tracking-widest">
                                            {item.unidad_medida}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right hidden lg:table-cell">
                                        <span className="font-black text-slate-900 text-sm">
                                            S/{" "}
                                            {item.precio_unitario?.toLocaleString(undefined, {
                                                minimumFractionDigits: 2,
                                            })}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-center gap-2 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors border border-transparent hover:border-blue-100"
                                                title="Editar"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(item)}
                                                className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors border border-transparent hover:border-rose-100"
                                                title="Eliminar"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-32 bg-slate-50/30">
                        <div className="relative mb-4">
                            <div className="animate-spin rounded-full h-14 w-14 border-b-2 border-indigo-600"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Package size={20} className="text-indigo-400 animate-pulse" />
                            </div>
                        </div>
                        <p className="text-[10px] font-black text-slate-400 animate-pulse uppercase tracking-[0.3em]">
                            Sincronizando catálogo...
                        </p>
                    </div>
                ) : filteredItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 bg-slate-50/50">
                        <div className="p-4 bg-white rounded-2xl shadow-sm mb-4 border border-slate-100">
                            <Plus size={32} className="text-slate-300" />
                        </div>
                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">
                            No se encontraron ítems
                        </h3>
                        <p className="text-xs text-slate-400 mt-1">
                            Gestiona tu catálogo de productos y servicios aquí.
                        </p>
                    </div>
                ) : null}

                {/* Paginación */}
                {filteredItems.length > 0 && (
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-600">
                                    Mostrar:
                                </span>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="h-9 px-3 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                            <span className="text-xs font-bold text-slate-600">
                                Mostrando {startIndex + 1} a{" "}
                                {Math.min(endIndex, filteredItems.length)} de{" "}
                                {filteredItems.length} ítems
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft size={18} className="text-slate-600" />
                            </button>

                            <div className="flex items-center gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter((page) => {
                                        // Mostrar solo páginas relevantes
                                        if (totalPages <= 7) return true;
                                        if (page === 1 || page === totalPages) return true;
                                        if (page >= currentPage - 1 && page <= currentPage + 1)
                                            return true;
                                        return false;
                                    })
                                    .map((page, index, array) => (
                                        <React.Fragment key={page}>
                                            {index > 0 && array[index - 1] !== page - 1 && (
                                                <span className="px-2 text-slate-400">...</span>
                                            )}
                                            <button
                                                onClick={() => goToPage(page)}
                                                className={`min-w-[36px] h-9 px-3 rounded-lg text-xs font-black transition-all ${currentPage === page
                                                    ? "bg-indigo-600 text-white shadow-sm"
                                                    : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        </React.Fragment>
                                    ))}
                            </div>

                            <button
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight size={18} className="text-slate-600" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ItemCatalogoModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={loadItems}
                item={selectedItem}
            />
            <ItemCatalogoImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onSuccess={loadItems}
            />

            {/* Confirmación de Eliminación */}
            {
                itemToDelete && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
                            <div className="p-8 text-center text-slate-900">
                                <div className="mx-auto w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mb-6 transition-transform hover:scale-110">
                                    <Trash2 className="w-8 h-8 text-rose-500" />
                                </div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-widest mb-2">
                                    ¿Eliminar Ítem?
                                </h3>

                                {!deleteFeedback ? (
                                    <p className="text-slate-500 text-sm font-medium leading-relaxed">
                                        Estás a punto de eliminar{" "}
                                        <span className="text-slate-900 font-bold">
                                            "{itemToDelete.nombre}"
                                        </span>
                                        . Esta acción es definitiva y podría afectar tus planes de
                                        mantenimiento.
                                    </p>
                                ) : (
                                    <div className="text-left space-y-4">
                                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                                            <p className="text-amber-800 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
                                                <AlertCircle size={14} />
                                                Acción Bloqueada
                                            </p>
                                            <p className="text-amber-900 text-sm font-medium leading-relaxed">
                                                {deleteFeedback.message}
                                            </p>
                                        </div>

                                        {deleteFeedback.planes && (
                                            <div className="space-y-2">
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Planes Asociados:</p>
                                                <div className="flex flex-wrap gap-2">
                                                    {deleteFeedback.planes.map(plan => (
                                                        <span key={plan} className="px-3 py-1 bg-slate-100 text-slate-700 text-[10px] font-bold rounded-lg uppercase tracking-wider">
                                                            {plan}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="flex-1 h-12 font-black text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:bg-slate-200/50 transition-all rounded-xl"
                                    onClick={() => {
                                        setItemToDelete(null);
                                        setDeleteFeedback(null);
                                    }}
                                    disabled={isDeleting}
                                >
                                    {deleteFeedback ? "Entendido" : "Cancelar"}
                                </Button>
                                {!deleteFeedback && (
                                    <Button
                                        type="button"
                                        className="flex-1 h-12 bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-200/50 font-black text-[10px] uppercase tracking-[0.2em] border-none transition-all hover:scale-[1.02] active:scale-[0.98] rounded-xl"
                                        onClick={confirmDelete}
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? (
                                            <div className="flex items-center gap-2">
                                                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                Borrando...
                                            </div>
                                        ) : (
                                            "Eliminar"
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Bulk Delete Confirmation Modal */}
            {
                showBulkDeleteConfirm && (
                    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200 border border-slate-200">
                            <div className="p-8 text-center text-slate-900">
                                <div className="mx-auto w-16 h-16 bg-rose-50 rounded-2xl flex items-center justify-center mb-6 transition-transform hover:scale-110">
                                    <AlertCircle className="w-8 h-8 text-rose-500" />
                                </div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-widest mb-2">
                                    ¿Eliminar Varios Ítems?
                                </h3>

                                {!deleteFeedback ? (
                                    <p className="text-slate-500 text-sm font-medium leading-relaxed">
                                        Estás a punto de eliminar <span className="text-rose-600 font-bold">{selectedIds.length} ítems</span> de forma masiva. Esta acción no se puede deshacer.
                                    </p>
                                ) : (
                                    <div className="text-left space-y-4">
                                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                                            <p className="text-amber-800 text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
                                                <AlertCircle size={14} />
                                                Acción Bloqueada
                                            </p>
                                            <p className="text-amber-900 text-sm font-medium leading-relaxed">
                                                {deleteFeedback.message}
                                            </p>
                                        </div>

                                        <div className="max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                                            {deleteFeedback.planes && (
                                                <div className="space-y-2">
                                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Elementos con dependencias:</p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {deleteFeedback.planes.map(item => (
                                                            <span key={item} className="px-2 py-1 bg-slate-100 text-slate-700 text-[10px] font-bold rounded-lg border border-slate-200 uppercase">
                                                                {item}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {deleteFeedback.detalles && Object.entries(deleteFeedback.detalles).map(([item, planes]) => (
                                                <div key={item} className="mb-4 last:mb-0">
                                                    <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest mb-2">{item}:</p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {planes.map(plan => (
                                                            <span key={plan} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[9px] font-bold rounded-md uppercase tracking-wider border border-slate-200">
                                                                {plan}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="flex-1 h-12 font-black text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900 hover:bg-slate-200/50 transition-all rounded-xl"
                                    onClick={() => {
                                        setShowBulkDeleteConfirm(false);
                                        setDeleteFeedback(null);
                                    }}
                                    disabled={isBulkDeleting}
                                >
                                    {deleteFeedback ? "Entendido" : "Cancelar"}
                                </Button>
                                {!deleteFeedback && (
                                    <Button
                                        type="button"
                                        className="flex-1 h-12 bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-200/50 font-black text-[10px] uppercase tracking-[0.2em] transition-all hover:scale-[1.02] active:scale-[0.98] rounded-xl"
                                        onClick={handleBulkDelete}
                                        disabled={isBulkDeleting}
                                    >
                                        {isBulkDeleting ? "Borrando..." : `Sí, Eliminar ${selectedIds.length}`}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default CatalogoItems;
